import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatUSD, formatLBP } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Settings, Sliders, Trash2, Users, X } from "lucide-react";

const requestId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const money = (v, c) =>
  c === "USD" ? `$${formatUSD(v)}` : `${formatLBP(v)} ل.ل`;
const activityLabels = {
  BOX_CREATED: "إنشاء الصندوق",
  BOX_FUNDED: "إضافة أموال",
  FAMILY_CONTRIBUTION: "مساهمة عائلية",
  BOX_WITHDRAWN: "إعادة إلى رصيد البنك",
  BOX_SPENT: "مصروف من الصندوق",
  BOX_BALANCE_ADJUSTED: "تصحيح رصيد بواسطة المدير",
  FAMILY_CONTRIBUTION_RETURNED: "إعادة مساهمة",
  GOAL_75_PERCENT: "وصل الهدف إلى 75%",
  GOAL_COMPLETED: "اكتمل الهدف",
  BOX_LOCKED: "قفل الصندوق",
  BOX_UNLOCKED: "فتح الصندوق",
};

export default function BoxesAdminPage() {
  const [data, setData] = useState({
      boxes: [],
      users: [],
      config: { limits: {} },
    }),
    [loading, setLoading] = useState(true),
    [query, setQuery] = useState(""),
    [filter, setFilter] = useState("ALL"),
    [selected, setSelected] = useState(null),
    [settings, setSettings] = useState(false),
    [creating, setCreating] = useState(false),
    [error, setError] = useState("");
  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const { data: next } = await adminApi.get("/admin/boxes");
      setData(next);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذر تحميل الصناديق.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const rows = useMemo(
    () =>
      data.boxes.filter(
        (b) =>
          (filter === "ALL" || b.type === filter || b.scope === filter) &&
          (b.name.toLowerCase().includes(query.toLowerCase()) ||
            b.owner?.username?.toLowerCase().includes(query.toLowerCase())),
      ),
    [data.boxes, query, filter],
  );
  const remove = async (box) => {
    if (
      !window.confirm(
        `حذف ${box.name}؟ ستعاد كل أمواله إلى صاحبه وتبقى السجلات.`,
      )
    )
      return;
    try {
      await adminApi.delete(`/admin/boxes/${box.id}`, {
        data: { request_id: requestId("admin-delete") },
      });
      toast.success("حُذف الصندوق وأعيد الرصيد بأمان.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر الحذف.");
    }
  };
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="section-title text-2xl">إدارة الأهداف والصناديق</h2>
          <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
            الأرصدة، الأهداف، المساهمون، التدقيق، التصحيح والحدود.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCreating(true)}
            className="btn-navy rounded-xl px-4 py-2 text-xs font-bold"
          >
            إنشاء صندوق
          </button>
          <button
            onClick={() => setSettings(true)}
            className="rounded-xl border px-4 py-2 text-xs font-bold"
          >
            <Settings className="me-2 inline h-4 w-4" />
            سلوك الإكمال
          </button>
          <button onClick={load} className="rounded-xl border px-4 py-2">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </header>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="الصناديق النشطة" value={data.boxes.length} />
        <Stat
          label="العائلية"
          value={data.boxes.filter((x) => x.scope === "FAMILY").length}
        />
        <Stat
          label="الأهداف المكتملة"
          value={data.boxes.filter((x) => x.status === "COMPLETED").length}
        />
      </div>
      <div className="flex flex-wrap gap-2 rounded-2xl border bg-[var(--color-surface)] p-3">
        <div className="relative min-w-[14rem] flex-1">
          <Search className="absolute start-3 top-3 h-4 w-4 text-slate-400" />
          <input
            className="input-pro ps-9"
            placeholder="بحث بالاسم أو المالك"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className="input-pro w-auto"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="ALL">الكل</option>
          <option value="REGULAR">Boxes</option>
          <option value="GOAL">Goal Boxes</option>
          <option value="FAMILY">العائلية</option>
        </select>
      </div>
      {error ? (
        <div className="rounded-2xl border p-8 text-center text-red-600">
          {error}
        </div>
      ) : loading ? (
        <div className="p-10 text-center">جارٍ التحميل…</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((box) => (
            <button
              key={box.id}
              onClick={() => setSelected(box)}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-start"
            >
              <div className="flex justify-between gap-2">
                <div>
                  <b>
                    {box.icon} {box.name}
                  </b>
                  <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                    {box.owner?.display_name} (@{box.owner?.username})
                  </p>
                </div>
                <span className="text-[10px] font-bold">
                  {box.type} · {box.scope}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <span className="rounded-xl bg-[var(--color-surface-secondary)] p-3">
                  {money(box.balances.USD, "USD")}
                </span>
                <span className="rounded-xl bg-[var(--color-surface-secondary)] p-3">
                  {money(box.balances.LBP, "LBP")}
                </span>
              </div>
              {box.progress && (
                <p className="mt-3 text-xs font-bold">
                  التقدم {box.progress.percentage}% · المتبقي{" "}
                  {money(box.progress.remaining_amount, box.progress.currency)}
                </p>
              )}
            </button>
          ))}
        </div>
      )}
      {selected && (
        <AdminBoxModal
          box={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            load();
          }}
          onDelete={() => remove(selected)}
        />
      )}{" "}
      {settings && (
        <SettingsModal
          data={data}
          onClose={() => setSettings(false)}
          onDone={() => {
            setSettings(false);
            load();
          }}
        />
      )}
      {creating && (
        <CreateAdminBoxModal
          users={data.users}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}
const Stat = ({ label, value }) => (
  <div className="rounded-2xl border bg-[var(--color-surface)] p-4">
    <small>{label}</small>
    <b className="mt-1 block text-2xl">{value}</b>
  </div>
);
function AdminBoxModal({ box, onClose, onDone, onDelete }) {
  const [mode, setMode] = useState("adjust"),
    [form, setForm] = useState({
      currency: "USD",
      amount: "",
      reason: "",
      user_id: box.contributor_summary?.[0]?.user_id || "",
    }),
    [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "adjust" ? "adjustments" : "refunds";
      await adminApi.post(`/admin/boxes/${box.id}/${path}`, {
        ...form,
        request_id: requestId(path),
      });
      toast.success(
        mode === "adjust"
          ? "تم التصحيح مع سجل تدقيق."
          : "أعيدت المساهمة بعملية مالية موثقة.",
      );
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التنفيذ.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title={box.name} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <span className="rounded-xl bg-[var(--color-surface-secondary)] p-3">
            {money(box.balances.USD, "USD")}
          </span>
          <span className="rounded-xl bg-[var(--color-surface-secondary)] p-3">
            {money(box.balances.LBP, "LBP")}
          </span>
        </div>
        {box.scope === "FAMILY" && (
          <div>
            <h4 className="font-black">المساهمون</h4>
            {box.contributor_summary.map((r, i) => (
              <div
                key={i}
                className="mt-2 flex justify-between rounded-xl border p-2 text-xs"
              >
                <span>{r.display_name}</span>
                <b>{money(r.total_amount, r.currency)}</b>
              </div>
            ))}
          </div>
        )}
        <section>
          <h4 className="font-black">سجل النشاط والتدقيق</h4>
          <div className="mt-2 max-h-44 space-y-2 overflow-y-auto">
            {box.activity?.length ? (
              box.activity.map((row) => (
                <div key={row.id} className="rounded-xl border border-[var(--color-border)] p-3 text-xs">
                  <b>{activityLabels[row.action] || row.action}</b>
                  <span className="mt-1 block text-[var(--color-text-secondary)]">
                    {row.actor_name_snapshot} · {new Date(row.created_at).toLocaleString("ar-LB")}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-xs text-[var(--color-text-secondary)]">لا يوجد نشاط مسجل.</p>
            )}
          </div>
        </section>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("adjust")}
              className={`rounded-xl border p-2 ${mode === "adjust" ? "bg-slate-950 text-white" : ""}`}
            >
              <Sliders className="me-1 inline h-4 w-4" />
              تصحيح رصيد
            </button>
            <button
              type="button"
              disabled={box.scope !== "FAMILY"}
              onClick={() => setMode("refund")}
              className={`rounded-xl border p-2 disabled:opacity-30 ${mode === "refund" ? "bg-slate-950 text-white" : ""}`}
            >
              <Users className="me-1 inline h-4 w-4" />
              إعادة مساهمة
            </button>
          </div>
          {mode === "refund" && (
            <select
              required
              className="input-pro"
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
            >
              <option value="">اختر المستخدم</option>
              {box.contributor_summary.map((r) => (
                <option key={r.user_id} value={r.user_id}>
                  {r.display_name}
                </option>
              ))}
            </select>
          )}
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <input
              required
              type="number"
              step={form.currency === "USD" ? "0.01" : "5000"}
              className="input-pro"
              placeholder={mode === "adjust" ? "التغيير (+ أو -)" : "المبلغ"}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
            <select
              className="input-pro"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              <option>USD</option>
              <option>LBP</option>
            </select>
          </div>
          <textarea
            required
            className="input-pro"
            placeholder="السبب الإلزامي"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
          <button
            disabled={loading}
            className="btn-navy w-full rounded-xl py-3"
          >
            تأكيد العملية الموثقة
          </button>
        </form>
        <button
          onClick={onDelete}
          className="w-full rounded-xl border border-red-200 py-2 text-red-600"
        >
          <Trash2 className="me-2 inline h-4 w-4" />
          حذف وإعادة الرصيد
        </button>
      </div>
    </Modal>
  );
}
function SettingsModal({ data, onClose, onDone }) {
  const [form, setForm] = useState({
      unlock_completed_goals: data.config.unlock_completed_goals !== false,
    }),
    [loading, setLoading] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.put("/admin/boxes/config", {
        ...form,
        request_id: requestId("config"),
      });
      toast.success("تم حفظ سلوك الصناديق.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title="سلوك الصناديق" onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <p className="text-sm text-[var(--color-text-secondary)]">تُطبّق الحدود المصرفية المشتركة تلقائيًا وفق سياسة الاشتراك.</p>
        <label className="flex items-center justify-between rounded-xl border p-3 text-sm font-bold"><span>فتح الهدف المكتمل تلقائياً</span><input type="checkbox" checked={form.unlock_completed_goals} onChange={(e) => setForm({ ...form, unlock_completed_goals: e.target.checked })}/></label>
        <button disabled={loading} className="btn-navy w-full rounded-xl py-3">
          حفظ الحدود
        </button>
      </form>
    </Modal>
  );
}
function CreateAdminBoxModal({ users, onClose, onDone }) {
  const [form, setForm] = useState({
      owner_id: users[0]?.id || "",
      name: "",
      type: "REGULAR",
      scope: "PERSONAL",
      target_currency: "USD",
      target_amount: "",
      lock_mode: "UNLOCKED",
      member_ids: [],
    }),
    [loading, setLoading] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.post("/admin/boxes", {
        ...form,
        request_id: requestId("admin-create"),
      });
      toast.success("أنشأ المدير الصندوق وحُفظ للمالك.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إنشاء الصندوق.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal title="إنشاء صندوق لمستخدم" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <select
          required
          className="input-pro"
          value={form.owner_id}
          onChange={(e) =>
            setForm({
              ...form,
              owner_id: e.target.value,
              member_ids: form.member_ids.filter((id) => id !== e.target.value),
            })
          }
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name} (@{u.username})
            </option>
          ))}
        </select>
        <input
          required
          className="input-pro"
          placeholder="اسم الصندوق"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <select
            className="input-pro"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            <option value="REGULAR">Box</option>
            <option value="GOAL">Goal Box</option>
          </select>
          <select
            className="input-pro"
            value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value })}
          >
            <option value="PERSONAL">شخصي</option>
            <option value="FAMILY">عائلي</option>
          </select>
        </div>
        {form.scope === "FAMILY" && (
          <div>
            <label className="text-xs font-bold">المشاركون</label>
            <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-xl border border-[var(--color-border)] p-2">
              {users
                .filter((user) => user.id !== form.owner_id)
                .map((user) => (
                  <label key={user.id} className="flex min-h-11 items-center gap-2 rounded-lg px-2 hover:bg-[var(--color-surface-secondary)]">
                    <input
                      type="checkbox"
                      checked={form.member_ids.includes(user.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          member_ids: e.target.checked
                            ? [...form.member_ids, user.id]
                            : form.member_ids.filter((id) => id !== user.id),
                        })
                      }
                    />
                    <span className="text-sm">{user.display_name} (@{user.username})</span>
                  </label>
                ))}
            </div>
          </div>
        )}
        {form.type === "GOAL" && (
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <input
              required
              type="number"
              step={form.target_currency === "USD" ? "0.01" : "5000"}
              className="input-pro"
              placeholder="الهدف"
              value={form.target_amount}
              onChange={(e) =>
                setForm({ ...form, target_amount: e.target.value })
              }
            />
            <select
              className="input-pro"
              value={form.target_currency}
              onChange={(e) =>
                setForm({ ...form, target_currency: e.target.value })
              }
            >
              <option>USD</option>
              <option>LBP</option>
            </select>
          </div>
        )}
        <button
          disabled={loading || !users.length}
          className="btn-navy w-full rounded-xl py-3"
        >
          إنشاء وحفظ
        </button>
      </form>
    </Modal>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3"
      onMouseDown={onClose}
    >
      <div
        className="max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-3xl bg-[var(--color-surface)] p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex justify-between">
          <h3 className="text-xl font-black">{title}</h3>
          <button onClick={onClose} className="h-11 w-11 rounded-xl border border-[var(--color-border)]">
            <X />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
